import React, { useEffect, useRef } from 'react'
import { Animated, Easing, StyleProp, ViewStyle } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ButtonSize, IconButton, IconName } from '@components/base'

const BORDER_RADIUS = {
  xs: 12,
  s: 16,
  m: 24,
  l: 32,
  xl: 32,
}

interface MicrophoneButtonProps {
  accessibilityLabel?: string
  // Applied to the pressable itself, above the wrapper's animated background.
  // Use this for a solid state color (e.g. muted) that should win over the
  // listening pulse rather than blend with it.
  buttonStyle?: StyleProp<ViewStyle>
  containerStyle?: StyleProp<ViewStyle>
  disabled?: boolean
  iconColor?: string
  iconName?: IconName
  isListening: boolean
  onPress: () => void | Promise<void>
  size?: ButtonSize
  variant?: 'primary' | 'secondary'
}

export const MicrophoneButton = ({
  accessibilityLabel = 'Voice input',
  buttonStyle,
  containerStyle,
  disabled = false,
  iconColor,
  iconName = 'microphone',
  isListening,
  onPress,
  size = 's',
  variant = 'primary',
}: MicrophoneButtonProps) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const colorAnimation = useRef(new Animated.Value(0)).current

  const baseColor =
    variant === 'primary' ? theme['theme.primary.base'] : theme['theme.surface.base2']
  const activeColor = theme['theme.tooltip.link']

  useEffect(() => {
    if (isListening) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(colorAnimation, {
            toValue: 1,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.timing(colorAnimation, {
            toValue: 0,
            duration: 1000,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ]),
      )

      pulse.start()

      return () => pulse.stop()
    }

    colorAnimation.setValue(0)
  }, [activeColor, baseColor, colorAnimation, isListening])

  const animatedBackgroundColor = colorAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [baseColor, activeColor],
  })

  // IconButton always prepends its own `${type}Icon` color, and react-native-svg
  // resolves a style `color` ahead of the `color` prop. So an explicit
  // `iconColor` has to win as a style override, not just as a prop.
  const variantIconStyle = variant === 'primary' ? styles.icon : styles.secondaryIcon
  // `IconProps['style']` is typed as ViewStyle, but Icon reads `color` off the
  // flattened style at runtime, which is how the variant styles above work too.
  const iconStyle = (iconColor ? { color: iconColor } : variantIconStyle) as StyleProp<ViewStyle>

  return (
    <Animated.View
      style={[
        styles.wrapper,
        { borderRadius: BORDER_RADIUS[size] },
        containerStyle,
        { backgroundColor: animatedBackgroundColor },
        disabled && styles.disabled,
      ]}
    >
      <IconButton
        accessibilityLabel={accessibilityLabel}
        disabled={disabled}
        iconName={iconName}
        onPress={onPress}
        size={size}
        style={buttonStyle}
        type="transparent"
        iconProps={{ color: iconColor, style: iconStyle }}
      />
    </Animated.View>
  )
}

const themedStyles = StyleService.create({
  wrapper: {
    borderRadius: 16,
  },
  disabled: {
    opacity: 0.4,
  },
  icon: {
    color: 'theme.solid.white',
  },
  secondaryIcon: {
    color: 'theme.text.secondary',
  },
})
